import * as React from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';

import Spinner from 'components/spinner/Spinner';
import SVGIcon from 'components/svg-icon/SVGIcon';
import Popover from 'components/popover/Popover';

import { __t, applyTranslations } from 'utils/language';
import { getMetricValueInPercentage } from 'utils/helper/common';
import { computeDisplayedMetricValue } from 'utils/helper/metrics';
import { getFormattedFollowersValue } from 'utils/influencers';
import { getExternalUrl } from 'utils/url';
import { insertIf } from 'utils/list';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  getUserNameFromChannel
} from 'utils/channels';
import {
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO
} from 'utils/deliverables';

import './creator-performance-channels.scss';

import type { ChannelShort } from 'types';
import type { CreatorPerformance } from '../../types/CreatorPerformance.type';

applyTranslations(require('locales/common/collabary-channels.i18n.json'));
applyTranslations(require('./creator-performance-channels.i18n.json'));

type NumString = number | string;

type Props = {
  creatorPerformance: CreatorPerformance,
  isLoading: boolean
};

class CreatorPerformanceChannels extends React.PureComponent<Props> {
  getClassname(identifier: string, type?: string) {
    return classnames({
      [`creator-performance-channels__${identifier}`]: Boolean(identifier),
      [`creator-performance-channels__${identifier}--type-${String(type)}`]: Boolean(type)
    });
  }

  getContentType() {
    const { creatorPerformance } = this.props;
    const deliverableTypesCount = creatorPerformance.agreedDeliverableTypes.length;
    let type;

    if (deliverableTypesCount === 1) {
      type = 'single';
    } else if (deliverableTypesCount >= 2) {
      type = 'multi';
    }

    return type;
  }

  renderFollowers = (channel: ChannelShort): NumString => {
    const value = channel.followers ? getFormattedFollowersValue(channel.followers) : null;
    return computeDisplayedMetricValue(value, 'followers', 0);
  };

  renderEngagementRate = (channel: ChannelShort): NumString => {
    const value = channel.engagementRate ? channel.engagementRate : null;
    return getMetricValueInPercentage(value, { hasThousandsSeparator: true, defaultValue: 'n/a' });
  };

  renderIcon = (channel: ChannelShort): React.ReactNode => (
    <SVGIcon
      name={`${channel.type}-no-customisation`}
      width={16}
      height={16}
      link={getExternalUrl(channel.url)}
      className={this.getClassname('icon')}
    />
  );

  renderInfo(channel?: ChannelShort | null): React.ReactNode | undefined | null {
    if (isNil(channel)) {
      return null;
    }
    const userName = getUserNameFromChannel(channel);
    const userNameLabel = userName ? `@${userName}` : '';

    return (
      <div
        key={`creator-performance-channels__body-item-${channel.type}`}
        className={this.getClassname('item-container')}
      >
        <div className={this.getClassname('item-wrapper')}>
          <Popover
            triggerComponent={(
              <React.Fragment>
                <div className={this.getClassname('item')}>
                  {this.renderFollowers(channel)}
                </div>
                <div className={`${this.getClassname('item')} ${this.getClassname('divider')}`}>
                  |
                </div>
                <div className={this.getClassname('item')}>
                  {this.renderEngagementRate(channel)}
                </div>
              </React.Fragment>
            )}
            classNameWrapper="creator-performance-channels__metric-popover"
            classNamePopover="creator-performance-channels__metric-popover-content"
          >
            <div>
              <div className={this.getClassname('popover-header')}>
                {__t(`collabary_channels.${channel.type}.label`)}
              </div>
              <div className={this.getClassname('popover-item')}>
                {`${__t('creator_performance_channels.label_followers')}: `}
                {this.renderFollowers(channel)}
              </div>
              <div className={this.getClassname('popover-item')}>
                {`${__t('creator_performance_channels.label_engagement')}: `}
                {this.renderEngagementRate(channel)}
              </div>
            </div>
          </Popover>
        </div>
        <div className={this.getClassname('channel')}>
          <div className={`${this.getClassname('item')} ${this.getClassname('channel-icon')}`}>
            {this.renderIcon(channel)}
          </div>
          <div className={this.getClassname('channel-name')}>
            {userNameLabel}
          </div>
        </div>
      </div>
    );
  }

  renderContent(): JSX.Element {
    const { creatorPerformance } = this.props;

    const availableChannels = [
      ...insertIf(
        creatorPerformance.agreedDeliverableTypes?.includes(DELIVERABLE_TYPE_INSTAGRAM_STORY)
          || creatorPerformance.agreedDeliverableTypes?.includes(DELIVERABLE_TYPE_INSTAGRAM_POST)
          || creatorPerformance.agreedDeliverableTypes?.includes(DELIVERABLE_TYPE_INSTAGRAM_REEL),
        CHANNEL_TYPE_INSTAGRAM
      ),
      ...insertIf(creatorPerformance.agreedDeliverableTypes?.includes(DELIVERABLE_TYPE_TIKTOK_VIDEO), CHANNEL_TYPE_TIKTOK)
    ];

    return (
      <div className={this.getClassname('content', this.getContentType())}>
        <div className={this.getClassname('content-inner', availableChannels.length === 1 ? 'centered' : '')}>
          {
            availableChannels?.map(channel => (
              this.renderInfo(creatorPerformance.channels?.find(ch => ch.type === channel))
            ))
          }
        </div>
      </div>
    );
  }

  renderSpinner(): JSX.Element {
    return (
      <div className="creator-performance-channels__spinner-wrapper">
        <div className="creator-performance-channels__spinner">
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { isLoading } = this.props;

    return (
      <div className="creator-performance-channels">
        <div className={this.getClassname('title')}>
          {__t('creator_performance_channels.title')}
        </div>
        {
          isLoading
            ? this.renderSpinner()
            : this.renderContent()
        }
      </div>
    );
  }
}

export default CreatorPerformanceChannels;
